package deckgl

import js.objects.unsafeJso

external interface DashedPathLayerProps<D> : PathLayerProps<D> {
    /**
     * The dash array to draw each path with: [dashSize, gapSize] relative to the width of the path.
     */
    var getDashArray: dynamic

    /**
     * Only effective if getDashArray is specified. If true, adjust gaps for the dashes to align at both ends. Overrides the effect of highPrecisionDash.
     */
    var dashJustified: Boolean

    /**
     * The offset to draw each path with, relative to the width of the path. Negative offset is to the left hand side, and positive offset is to the right hand side. 0 extrudes the path so that it is centered at the specified coordinates.
     */
    var getOffset: dynamic

    /**
     * Only effective if getDashArray is specified. If true, gaps between solid strokes are pickable. If false, only the solid strokes are pickable.
     */
    var dashGapPickable: dynamic

    var dashStart: dynamic
}

// Uniform block holding the animated dash phase (deck.gl 9.1+ no longer supports loose uniforms)
private val dashFlowUniforms: dynamic = unsafeJso<dynamic> {
    name = "dashFlow"
    vs = """
        layout(std140) uniform dashFlowUniforms {
          float dashStart;
        } dashFlow;
    """.trimIndent()
    uniformTypes = unsafeJso<dynamic> { dashStart = "f32" }
}

// luma.gl 9.4 shader plugin: bundles the uniform module with named, ordered injections.
// The order places the phase shift after PathStyleExtension has computed vDashOffset (order 0).
private val dashFlowPlugin: dynamic = unsafeJso<dynamic> {
    name = "dash-flow"
    modules = arrayOf(dashFlowUniforms)
    glsl = unsafeJso<dynamic> {
        injections = arrayOf(unsafeJso<dynamic> {
            target = "vs:#main-end"
            injection = "vDashOffset += dashFlow.dashStart;"
            order = 100
        })
    }
}

class DashedPathLayer<D>(props: DashedPathLayerProps<D>) : PathLayer<D>(props) {
    override fun getShaders(): dynamic {
        val shaders = super.getShaders()
        shaders.plugins = (shaders.plugins ?: emptyArray<Any>()).unsafeCast<Array<Any>>() + (dashFlowPlugin as Any)
        return shaders
    }

    override fun draw(opts: dynamic) {
        val dashStart = this.asDynamic().props?.dashStart ?: 0
        setShaderModuleProps(unsafeJso<dynamic> { dashFlow = unsafeJso<dynamic> { this.dashStart = dashStart } })
        super.draw(opts)
    }
}
